import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, CustomInput, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { updatePassword, setFundPassword, sendPasswordResetEmail } from '../../services/authService';
import { isValidPassword, isValidFundPassword } from '../../utils/helpers';
import './SecurityScreen.css';

export const SecurityScreen = () => {
    const navigate = useNavigate();
    const { userData, refreshUserData } = useAuth();
    const alert = useAlert();

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showFundPasswordModal, setShowFundPasswordModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Password change form
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Fund password form
    const [fundPwd, setFundPwd] = useState('');
    const [confirmFundPwd, setConfirmFundPwd] = useState('');

    const handleChangePassword = async () => {
        if (!isValidPassword(newPassword)) {
            alert.showError('Error', 'Password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            alert.showError('Error', 'Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            await updatePassword(newPassword);
            alert.showSuccess('Success', 'Password updated successfully');
            setShowPasswordModal(false);
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    const handleSetFundPassword = async () => {
        if (!isValidFundPassword(fundPwd)) {
            alert.showError('Error', 'Fund password must be 6 digits');
            return;
        }
        if (fundPwd !== confirmFundPwd) {
            alert.showError('Error', 'Fund passwords do not match');
            return;
        }

        setLoading(true);
        try {
            await setFundPassword(fundPwd);
            await refreshUserData();
            alert.showSuccess('Success', 'Fund password set successfully');
            setShowFundPasswordModal(false);
            setFundPwd('');
            setConfirmFundPwd('');
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to set fund password');
        } finally {
            setLoading(false);
        }
    };



    const securityItems = [
        {
            icon: 'lock',
            title: 'Login Password',
            subtitle: 'Change your account password',
            action: () => setShowPasswordModal(true),
            color: '#3B82F6',
        },
        {
            icon: 'pin',
            title: 'Fund Password',
            subtitle: userData?.fundPassword ? 'Change fund password' : 'Set fund password',
            action: () => setShowFundPasswordModal(true),
            color: '#D4AF37',
            badge: !userData?.fundPassword ? 'Required' : null,
        },

    ];

    return (
        <div className="security-screen">
            <LoadingOverlay visible={loading} message="Please wait..." />

            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">Security</h1>
                <div style={{ width: 40 }}></div>
            </div>

            <GlassCard className="security-info">
                <span className="material-icons shield-icon">verified_user</span>
                <div>
                    <h3>Account Security</h3>
                    <p>Keep your account safe by using a strong password and fund password</p>
                </div>
            </GlassCard>

            <div className="security-list">
                {securityItems.map((item, index) => (
                    <GlassCard
                        key={index}
                        className="security-item"
                        onClick={item.action}
                    >
                        <div
                            className="item-icon"
                            style={{ backgroundColor: `${item.color}15` }}
                        >
                            <span
                                className="material-icons"
                                style={{ color: item.color }}
                            >
                                {item.icon}
                            </span>
                        </div>
                        <div className="item-content">
                            <span className="item-title">{item.title}</span>
                            <span className="item-subtitle">{item.subtitle}</span>
                        </div>
                        {item.badge && (
                            <span className="item-badge">{item.badge}</span>
                        )}
                        <span className="material-icons item-arrow">chevron_right</span>
                    </GlassCard>
                ))}
            </div>

            <div className="security-tips">
                <h4>Security Tips</h4>
                <ul>
                    <li>Use a unique password for this account</li>
                    <li>Never share your fund password with anyone</li>
                    <li>Enable 2FA if available</li>
                </ul>
            </div>

            {/* Change Password Modal */}
            {showPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <GlassCard className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h3>Change Password</h3>

                        <CustomInput
                            label="New Password"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            isPassword
                            leftIcon="lock"
                        />

                        <CustomInput
                            label="Confirm Password"
                            placeholder="Re-enter password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            isPassword
                            leftIcon="lock"
                        />

                        <div className="modal-buttons">
                            <GradientButton
                                title="Cancel"
                                variant="outline"
                                onClick={() => setShowPasswordModal(false)}
                            />
                            <GradientButton
                                title="Update"
                                onClick={handleChangePassword}
                                loading={loading}
                            />
                        </div>
                    </GlassCard>
                </div>
            )}

            {/* Fund Password Modal */}
            {showFundPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowFundPasswordModal(false)}>
                    <GlassCard className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h3>{userData?.fundPassword ? 'Change' : 'Set'} Fund Password</h3>

                        <CustomInput
                            label="Fund Password (6 digits)"
                            placeholder="Enter 6-digit password"
                            value={fundPwd}
                            onChange={(e) => setFundPwd(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            isPassword
                            leftIcon="pin"
                        />

                        <CustomInput
                            label="Confirm Fund Password"
                            placeholder="Re-enter password"
                            value={confirmFundPwd}
                            onChange={(e) => setConfirmFundPwd(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            isPassword
                            leftIcon="pin"
                        />

                        <div className="modal-buttons">
                            <GradientButton
                                title="Cancel"
                                variant="outline"
                                onClick={() => setShowFundPasswordModal(false)}
                            />
                            <GradientButton
                                title={userData?.fundPassword ? 'Update' : 'Set'}
                                onClick={handleSetFundPassword}
                                loading={loading}
                            />
                        </div>
                    </GlassCard>
                </div>
            )}
        </div>
    );
};

export default SecurityScreen;
